import React, { useState, useEffect, useRef } from "react";
import { countries, CountryData } from "../../utils/countryData";

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredCountries, setFilteredCountries] = useState(countries);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const lowercasedSearch = search.toLowerCase();
    const filtered = countries.filter(
      (country) =>
        country.name.toLowerCase().includes(lowercasedSearch) ||
        country.code.includes(lowercasedSearch)
    );
    setFilteredCountries(filtered);
  }, [search]);

  const handleSelect = (country: CountryData) => {
    onChange(country.code);
    setIsOpen(false);
  };

  const selectedCountry = countries.find((country) => country.code === value);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className="mt-1 relative rounded-md shadow-sm cursor-pointer"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
          <img
            src={`https://flagcdn.com/w20/${selectedCountry?.alpha2.toLowerCase()}.png`}
            srcSet={`https://flagcdn.com/w40/${selectedCountry?.alpha2.toLowerCase()}.png 2x`}
            width="20"
            alt={`${selectedCountry?.name} flag`}
          />
        </div>
        <input
          type="text"
          className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-8 pr-8 text-sm sm:text-base border-gray-300 rounded-md"
          value={`${selectedCountry?.code}`}
          readOnly
          disabled={disabled}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            className="h-4 w-4 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-gray-600 bg-opacity-75 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs sm:max-w-sm overflow-hidden">
            <div className="p-2">
              <input
                type="text"
                className="block w-full px-3 py-2 text-sm text-gray-800 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Search countries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="overflow-y-auto max-h-60 sm:max-h-80 pb-2">
              {filteredCountries.map((country) => (
                <div
                  key={`${country.code}-${country.alpha2}-${country.name}`}
                  className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 text-sm"
                  onClick={() => handleSelect(country)}
                >
                  <div className="flex items-center">
                    <img
                      src={`https://flagcdn.com/w20/${country.alpha2.toLowerCase()}.png`}
                      srcSet={`https://flagcdn.com/w40/${country.alpha2.toLowerCase()}.png 2x`}
                      width="20"
                      alt={`${country.name} flag`}
                      className="flex-shrink-0 mr-3"
                    />
                    <span className="font-normal truncate">{country.name}</span>
                  </div>
                  <span className="text-gray-500 absolute inset-y-0 right-0 flex items-center pr-4">
                    {country.code}
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                onClick={() => setIsOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelect;
